import { usePlatform } from "@/platform/context"
import type {
	AppAuthSession,
	AppLoginInput,
	AppPlatformUser,
} from "@/platform/types"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { createContext, useCallback, useContext, useEffect } from "react"
import { type UserMe, useUserMe } from "../hooks/data/useSession"
import { apiClient, getData } from "../lib/apiClient"

type AuthStatus = "loading" | "authenticated" | "unauthenticated"
type WebAuthUser = NonNullable<UserMe["user"]>
type AuthUser = AppPlatformUser | WebAuthUser

const AuthContext = createContext<
	| {
			authToken?: string
			user: AuthUser | null
			status: AuthStatus
			initialised: boolean
			getGoogleSignInUrl: (redirectPath?: string) => string
			login: (input: AppLoginInput) => Promise<AppAuthSession | UserMe>
			logout: () => Promise<void>
	  }
	| undefined
>(undefined)

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
	const platform = usePlatform()
	const queryClient = useQueryClient()
	const hasPlatformAuth = !!platform.getStoredSession
	const user = useUserMe({ enabled: !hasPlatformAuth })
	const platformSession = useQuery({
		enabled: hasPlatformAuth,
		queryKey: ["auth", "platform-session", platform.kind],
		queryFn: async () => platform.getStoredSession?.() ?? null,
		retry: false,
	})

	useEffect(() => {
		if (!platform.onAuthEvent) return
		return platform.onAuthEvent((event) => {
			if (event.type === "session") {
				queryClient.setQueryData(
					["auth", "platform-session", platform.kind],
					event.session,
				)
				queryClient.setQueryData(["user", "me"], {
					user: event.session.user,
				})
				return
			}

			queryClient.setQueryData(
				["auth", "platform-session", platform.kind],
				null,
			)
			queryClient.setQueryData(["user", "me"], { user: null })
		})
	}, [platform, queryClient])

	const getGoogleSignInUrl = useCallback(
		(redirectPath?: string) => {
			if (platform.getGoogleSignInUrl) {
				return platform.getGoogleSignInUrl(redirectPath)
			}

			const search = new URLSearchParams()
			if (redirectPath?.startsWith("/")) {
				search.set("redirect", redirectPath)
			}
			const query = search.toString()
			return `${platform.apiOrigin}/auth/google/start${query ? `?${query}` : ""}`
		},
		[platform],
	)

	const login = useCallback(
		async (input: AppLoginInput) => {
			if (platform.login) {
				const session = await platform.login(input)
				queryClient.setQueryData(
					["auth", "platform-session", platform.kind],
					session,
				)
				queryClient.setQueryData(["user", "me"], { user: session.user })
				return session
			}

			const response = await apiClient.auth.login.$post({ json: input })
			const payload = await getData(response)
			void queryClient.invalidateQueries({ queryKey: ["user", "me"] })
			return payload
		},
		[platform, queryClient],
	)

	const logout = useCallback(async () => {
		if (platform.logout) {
			await platform.logout()
			queryClient.setQueryData(
				["auth", "platform-session", platform.kind],
				null,
			)
			queryClient.setQueryData(["user", "me"], { user: null })
			return
		}

		const response = await apiClient.auth.logout.$post()
		await getData(response)
		queryClient.setQueryData(["user", "me"], { user: null })
		void queryClient.invalidateQueries({ queryKey: ["user", "me"] })
	}, [platform, queryClient])

	const session = platformSession.data ?? null
	const sessionUser = hasPlatformAuth
		? (session?.user ?? null)
		: (user.data?.user ?? null)
	const authToken = session?.token
	const isSessionPending = hasPlatformAuth
		? platformSession.isPending
		: user.isPending
	const status: AuthStatus = isSessionPending
		? "loading"
		: sessionUser
			? "authenticated"
			: "unauthenticated"

	return (
		<AuthContext.Provider
			value={{
				authToken,
				user: sessionUser,
				initialised: !isSessionPending,
				getGoogleSignInUrl,
				login,
				logout,
				status,
			}}
		>
			{children}
		</AuthContext.Provider>
	)
}

export const useAuth = () => {
	const ctx = useContext(AuthContext)
	if (!ctx) {
		throw new Error("useAuth must be used within an AuthProvider")
	}
	return ctx
}
